import React, { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { usePermissions } from "./hooks/usePermissions";
import NavBar from "./components/NavBar";
import { FiChevronDown } from "react-icons/fi";
import EnviosWhatsappSenpulse from "./components/EnviosWhatsappSenpulse";
import SolicitudesPage from "./pages/SolicitudesPage";
import { TestingPayment } from "./components/TestingPayment";
import { VISTAS, GRUPO_ICONOS } from "./permisos/catalogoMenu";

// Carga perezosa del WebSocketLogsProvider
const WebSocketLogsProvider = lazy(() =>
  import("./components/WebSocketLogsProvider").then((module) => ({
    default: module.WebSocketLogsProvider,
  })),
);

// Componentes cargados con lazy loading
const LoginPage = lazy(() => import("./pages/LoginPage"));
const DashboardHome = lazy(() => import("./components/DashboardHome"));
const DashboardInvitado = lazy(() => import("./components/DashboardInvitado"));
const ListadoSolicitudes = lazy(
  () => import("./components/ListadoSolicitudes"),
);
const TextosEntrenamiento = lazy(
  () => import("./components/TextosEntrenamiento"),
);
const AnotacionesEntrenamiento = lazy(
  () => import("./components/AnotacionesEntrenamiento"),
);
const PlanesServicios = lazy(() => import("./components/PlanesServicios"));
const InformesDescarga = lazy(() => import("./components/InformesDescarga"));
const ListadoSolicitudesyCertificados = lazy(
  () => import("./components/ListadoSolicitudesyCertificados"),
);
const CreacionDeCodigoDeDescuento = lazy(
  () => import("./components/CreacionDeCodigoDeDescuento"),
);
const LoggerView = lazy(() => import("./components/LoggerView"));
const SeguimientoCorreo = lazy(() => import("./components/SeguimientoCorreo"));
const ComprasTimeline = lazy(() => import("./components/ComprasTimeline"));
const ColaPendienteCompra = lazy(
  () => import("./components/ColaPendienteCompra"),
);
const ListadoCtlPendientes = lazy(
  () => import("./components/ListadoCtlPendientes"),
);
const ParametrosAplicacion = lazy(
  () => import("./components/ParametrosAplicacion"),
);
const DashboardAlertas = lazy(() => import("./components/DashboardAlertas"));
const HistorialConsultasAntecedentes = lazy(
  () => import("./components/HistorialConsultasAntecedentes"),
);
const NuevaConsultaAntecedentes = lazy(
  () => import("./components/NuevaConsultaAntecedentes"),
);
const GestionConsumoTenants = lazy(
  () => import("./components/GestionConsumoTenants"),
);
const SuperAdminPanel = lazy(() => import("./components/SuperAdminPanel"));
const RegistroCivilPendientes = lazy(() => import("./components/RegistroCivilPendientes"));
const HistoricoVehicularPendientes = lazy(
  () => import("./components/HistoricoVehicularPendientes"),
);
const PredioConsultaInterna = lazy(
  () => import("./components/PredioConsultaInterna"),
);
const HistoricoVehicularConsultaInterna = lazy(
  () => import("./components/HistoricoVehicularConsultaInterna"),
);
const PreciosMercadoVehiculos = lazy(
  () => import("./pages/PreciosMercadoVehiculos"),
);
const AsignacionPermisos = lazy(
  () => import("./components/AsignacionPermisos"),
);
const FlujoDeCaja = lazy(() => import("./components/FlujoDeCaja"));

// Componente de carga
const LoadingComponent = () => (
  <div className="flex items-center justify-center h-screen w-full">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
      <p className="mt-4 text-gray-600">Cargando...</p>
    </div>
  </div>
);

const NavLinkItem = ({ to, icon: Icon, label, sidebarCollapsed, pathname, compacto }) => {
  const isActive = pathname === to;
  return (
    <Link
      to={to}
      className={`flex items-center transition-all duration-200 group relative
                ${sidebarCollapsed ? "justify-center px-0 py-4" : compacto ? "px-3 py-2" : "px-4 py-3"}
                ${
                  isActive
                    ? "bg-blue-600/10 text-lime-400 border-r-2 border-lime-400"
                    : "text-slate-400 hover:bg-slate-800/50 hover:text-white"
                }`}
    >
      <div
        className={`flex items-center justify-center transition-transform duration-200 ${isActive ? "scale-110" : "group-hover:scale-110"}`}
      >
        <Icon
          className={`${compacto ? "w-4 h-4" : "w-5 h-5"} flex-shrink-0 ${isActive ? "text-lime-400" : "text-slate-400 group-hover:text-white"}`}
        />
      </div>
      {!sidebarCollapsed && (
        <span
          className={`ml-3 transition-all duration-300 opacity-100 w-auto whitespace-nowrap ${
            compacto ? "text-[13px]" : "font-medium"
          }`}
        >
          {label}
        </span>
      )}

      {/* Tooltip for collapsed state (Popover style) */}
      {sidebarCollapsed && (
        <div className="absolute left-full ml-4 px-3 py-2 bg-[#1e293b] text-white text-xs font-semibold rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 translate-x-[-10px] group-hover:translate-x-0 whitespace-nowrap z-[100] border border-slate-700 shadow-2xl flex items-center">
          {/* Arrow */}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-[#1e293b]"></div>
          {/* Indicator for active state in popover */}
          {isActive && (
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
          )}
          {label}
        </div>
      )}
    </Link>
  );
};

/**
 * Grupo plegable del menú.
 *
 * Con el sidebar colapsado no se despliega en un flyout: el <nav> tiene
 * overflow-y-auto, que según la especificación fuerza overflow-x a auto también,
 * así que un panel posicionado hacia afuera quedaría cortado. En su lugar, tocar
 * el icono expande el sidebar y abre el grupo — comportamiento predecible y sin
 * depender de que el contenedor no recorte.
 */
const NavGroup = ({
  label,
  icon: Icon,
  items,
  sidebarCollapsed,
  pathname,
  preferencia,
  onToggle,
  onExpandirSidebar,
}) => {
  const contieneActiva = items.some((item) => item.to === pathname);
  const idPanel = `nav-grupo-${label.replace(/\s+/g, "-").toLowerCase()}`;

  // Sin preferencia guardada, el grupo de la ruta actual aparece abierto: al
  // entrar por URL directa se ve dónde estás parado. En cuanto el usuario lo
  // abre o lo cierra, manda su elección.
  const abierto = preferencia ?? contieneActiva;

  // Colapsado: el icono actúa como atajo para abrir el grupo ya expandido.
  if (sidebarCollapsed) {
    return (
      <button
        type="button"
        onClick={() => {
          onExpandirSidebar();
          if (!abierto) onToggle(abierto);
        }}
        title={label}
        className={`group relative flex w-full items-center justify-center px-0 py-4 transition-all duration-200
          ${contieneActiva ? "text-lime-400" : "text-slate-400 hover:bg-slate-800/50 hover:text-white"}`}
      >
        <Icon className="h-5 w-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
        {contieneActiva && (
          <span className="absolute right-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-l bg-lime-400" />
        )}
        <span className="pointer-events-none absolute left-full z-[100] ml-4 flex translate-x-[-10px] items-center whitespace-nowrap rounded-lg border border-slate-700 bg-[#1e293b] px-3 py-2 text-xs font-semibold text-white opacity-0 shadow-2xl transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100">
          <span className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-[#1e293b]" />
          {label}
        </span>
      </button>
    );
  }

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => onToggle(abierto)}
        aria-expanded={abierto}
        aria-controls={idPanel}
        className={`flex w-full items-center px-4 py-2.5 text-left transition-colors duration-200
          ${contieneActiva && !abierto ? "text-lime-400" : "text-slate-300 hover:text-white"}
          hover:bg-slate-800/40`}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="ml-3 flex-grow text-[11px] font-bold uppercase tracking-wider">
          {label}
        </span>
        {/* Punto indicador: avisa que hay una ruta activa adentro sin abrir el grupo. */}
        {contieneActiva && !abierto && (
          <span className="mr-2 h-1.5 w-1.5 rounded-full bg-lime-400 shadow-[0_0_8px_rgba(163,230,53,0.6)]" />
        )}
        <FiChevronDown
          className={`h-4 w-4 flex-shrink-0 text-slate-500 transition-transform duration-200 ${
            abierto ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* grid-rows animado: permite transición de altura sin fijar un valor en px,
          que se rompería al cambiar el número de ítems visibles por permisos.
          `invisible` al cerrar saca los enlaces del orden de tabulación y del
          árbol de accesibilidad: si no, se podría llegar con Tab a un menú que
          no se ve. */}
      <div
        id={idPanel}
        aria-hidden={!abierto}
        className={`grid transition-all duration-300 ease-in-out ${
          abierto ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 invisible"
        }`}
      >
        <div className="overflow-hidden">
          <div className="ml-[26px] border-l border-slate-800 pl-1">
            {items.map((item) => (
              <NavLinkItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                sidebarCollapsed={false}
                pathname={pathname}
                compacto
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente que sirve cada vista del catálogo. Vive fuera de App() porque no
// depende de estado del componente: es una tabla estática clave -> componente
// cargado con lazy loading.
const COMPONENTES_POR_VISTA = {
  predios_consultar: SolicitudesPage,
  predio_consulta_interna: PredioConsultaInterna,
  certificados_historico: ListadoSolicitudesyCertificados,
  certificados_ctl_pend: ListadoCtlPendientes,
  metricas_consultas: DashboardAlertas,
  vehicular_pendientes: HistoricoVehicularPendientes,
  vehicular_mercado: PreciosMercadoVehiculos,
  vehicular_consulta_interna: HistoricoVehicularConsultaInterna,
  rc_pendientes: RegistroCivilPendientes,
  consumo_clientes: GestionConsumoTenants,
  personas_nueva: NuevaConsultaAntecedentes,
  personas_historico: HistorialConsultasAntecedentes,
  monitoreo_whatsapp: EnviosWhatsappSenpulse,
  monitoreo_logs: LoggerView,
  monitoreo_correo: SeguimientoCorreo,
  monitoreo_compras_auto: ColaPendienteCompra,
  config_descuentos: CreacionDeCodigoDeDescuento,
  config_parametros: ParametrosAplicacion,
  config_planes: PlanesServicios,
  config_informes: InformesDescarga,
  flujo_caja: FlujoDeCaja,
  entrenamiento_textos: TextosEntrenamiento,
  superadmin_panel: SuperAdminPanel,
  superadmin_anotaciones: AnotacionesEntrenamiento,
  asignacion_permisos: AsignacionPermisos,
};

function App() {
  const { hasPermission, puedeVer, isAdmin, isSuperAdmin, user } = usePermissions();
  // En móvil el menú arranca cerrado; en escritorio se respeta la preferencia guardada.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) return true;
    return localStorage.getItem("sidebar-collapsed") === "true";
  });
  const location = useLocation();

  // Grupos abiertos del menú. Se recuerda entre sesiones para que cada quien deje
  // abierto lo que usa; si no hay preferencia guardada, el efecto de abajo abre el
  // grupo de la ruta actual.
  const [gruposAbiertos, setGruposAbiertos] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sidebar-grupos") || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem("sidebar-grupos", JSON.stringify(gruposAbiertos));
  }, [gruposAbiertos]);

  // Al alternar por primera vez se parte del estado visible (abierto si contiene
  // la ruta activa), no de "cerrado": si no, el primer clic sobre un grupo ya
  // abierto no haría nada aparente.
  const alternarGrupo = (label, visibleAhora) =>
    setGruposAbiertos((prev) => ({
      ...prev,
      [label]: !(prev[label] ?? visibleAhora),
    }));

  // Persistir la preferencia SOLO en escritorio: en móvil "colapsado" significa
  // "oculto", y guardarlo contaminaría la preferencia del usuario en desktop.
  useEffect(() => {
    if (window.innerWidth >= 768) {
      localStorage.setItem("sidebar-collapsed", sidebarCollapsed);
    }
  }, [sidebarCollapsed]);

  // Colapsar sidebar en móvil al cambiar de ruta
  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarCollapsed(true);
    }
  }, [location]);

  // En móvil con el menú abierto: bloquear scroll del fondo y cerrar con Esc.
  useEffect(() => {
    if (sidebarCollapsed || window.innerWidth >= 768) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSidebarCollapsed(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [sidebarCollapsed]);

  if (!user) {
    return (
      <Suspense fallback={<LoadingComponent />}>
        <LoginPage />
      </Suspense>
    );
  }

  /**
   * Menú y rutas generados desde el catálogo (Tarea 10): una sola fuente de
   * verdad por vista (VISTAS, en catalogoMenu.js) en vez de dos listas
   * paralelas que había que mantener sincronizadas a mano. Una clave sin
   * puedeVer(clave) no aparece en el menú NI genera <Route>.
   *
   * `metricas_consultas` es la única vista con ruta/componente condicional:
   * para super admin usa /dashboard + DashboardHome en vez de
   * /dashboard-alertas + DashboardAlertas, preservando el comportamiento que
   * ya existía (evita el bucle de redirección entre "/", "/dashboard" y
   * "/dashboard-alertas" en el catch-all).
   *
   * `vista.permiso`, si está presente, es la clave del catálogo que decide
   * puedeVer() en vez de la propia key del objeto (ver comentario de
   * `asignacion_permisos` en catalogoMenu.js): permite una entrada de menú
   * sin clave propia en el backend, gateada por una vista real existente.
   */
  const MENU_CONFIG = [];
  const RUTAS_POR_VISTA = new Map(); // ruta -> Componente, ya deduplicado
  let grupoEnCurso = null;

  for (const [clave, vista] of Object.entries(VISTAS)) {
    if (!puedeVer(vista.permiso ?? clave)) continue;

    const esMetricasSuperAdmin = clave === "metricas_consultas" && isSuperAdmin;
    const ruta = esMetricasSuperAdmin ? "/dashboard" : vista.ruta;
    const Componente = esMetricasSuperAdmin
      ? DashboardHome
      : COMPONENTES_POR_VISTA[clave];

    if (!RUTAS_POR_VISTA.has(ruta)) {
      RUTAS_POR_VISTA.set(ruta, Componente);
    }

    const itemMenu = { to: ruta, icon: vista.icono, label: vista.label };

    if (!vista.grupo) {
      MENU_CONFIG.push({ type: "item", ...itemMenu });
      grupoEnCurso = null;
      continue;
    }

    if (grupoEnCurso && grupoEnCurso.label === vista.grupo) {
      grupoEnCurso.items.push(itemMenu);
    } else {
      grupoEnCurso = {
        type: "section",
        label: vista.grupo,
        icon: GRUPO_ICONOS[vista.grupo],
        items: [itemMenu],
      };
      MENU_CONFIG.push(grupoEnCurso);
    }
  }


  return (
    <div className="w-full bg-slate-50 min-h-screen">
      <NavBar
        setSidebarCollapsed={setSidebarCollapsed}
        sidebarCollapsed={sidebarCollapsed}
      />
      <Suspense fallback={<LoadingComponent />}>
        <WebSocketLogsProvider tipoUsuario={isAdmin ? "admin" : "invitado"}>
          <div className="flex h-screen w-full relative pt-16">
            {/* Overlay para móviles cuando el menú está abierto */}
            {!sidebarCollapsed && (
              <div
                aria-hidden="true"
                className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-20 md:hidden transition-opacity duration-300 ease-in-out"
                onClick={() => setSidebarCollapsed(true)}
              />
            )}
            <div className="flex w-full h-full">
              {/* Sidebar */}
              <aside
                id="sidebar-menu"
                className={`bg-[#0f172a] text-slate-300 flex flex-col transition-all duration-300 ease-in-out border-r border-slate-800
                                ${sidebarCollapsed ? "md:w-16 w-64" : "md:w-64 w-64"}  
                                ${sidebarCollapsed ? "-translate-x-full md:translate-x-0" : "translate-x-0"} 
                                fixed left-0 top-16 bottom-0 z-30`}
              >
                <nav className="mt-4 flex-grow overflow-y-auto custom-scrollbar">
                  {MENU_CONFIG.map((item, index) => {
                    // Sin filtro aquí: MENU_CONFIG ya solo contiene claves con
                    // puedeVer(clave) === true (construido más arriba).

                    // Renderizar Sección como grupo plegable
                    if (item.type === "section") {
                      return (
                        <NavGroup
                          key={index}
                          label={item.label}
                          icon={item.icon}
                          items={item.items}
                          sidebarCollapsed={sidebarCollapsed}
                          pathname={location.pathname}
                          preferencia={gruposAbiertos[item.label]}
                          onToggle={(visible) => alternarGrupo(item.label, visible)}
                          onExpandirSidebar={() => setSidebarCollapsed(false)}
                        />
                      );
                    }

                    // Renderizar Item Individual
                    return (
                      <NavLinkItem
                        key={index}
                        to={item.to}
                        icon={item.icon}
                        label={item.label}
                        sidebarCollapsed={sidebarCollapsed}
                        pathname={location.pathname}
                      />
                    );
                  })}
                </nav>

                {/* Sidebar Footer or Version */}
                {!sidebarCollapsed && (
                  <div className="p-4 border-t border-slate-800 text-[10px] text-slate-500 font-medium">
                    &copy; 2024 LegalCheck Pro v1.0
                  </div>
                )}
              </aside>
              {/* Main */}
              <main
                className={`flex-1 transition-all duration-300 ease-in-out overflow-y-auto w-full bg-slate-50 ${sidebarCollapsed ? "md:ml-16" : "md:ml-64"}`}
              >
                <div className="p-4 sm:p-6 lg:p-8">
                  <div className="mx-auto">
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <Navigate
                            to={
                              isSuperAdmin ? "/dashboard" : "/dashboard-alertas"
                            }
                            replace
                          />
                        }
                      />

                      {/* Rutas generadas desde el catálogo (Tarea 10): una <Route> por
                          cada ruta única resultante de RUTAS_POR_VISTA, ya deduplicada
                          y filtrada por puedeVer(clave) al construir el mapa arriba. */}
                      {Array.from(RUTAS_POR_VISTA.entries()).map(([ruta, Componente]) => (
                        <Route key={ruta} path={ruta} element={<Componente />} />
                      ))}

                      {/* Rutas sin clave equivalente en el catálogo (ver task-10-report.md):
                          se dejan exactamente como estaban, con su gate original. */}
                      {hasPermission("antecedentes", "can_view_antecedentes") && (
                        <Route path="/testing" element={<TestingPayment />} />
                      )}
                      {/* Misma capacidad que el timeline embebido en ColaPendienteCompra
                          (gated por accion_compras_monitoreo_vivo): antes usaba isSuperAdmin,
                          asi que revocar el permiso a un super_admin no impedia entrar aqui. */}
                      {puedeVer('accion_compras_monitoreo_vivo') && (
                        <Route
                          path="/compras-timeline"
                          element={<ComprasTimeline />}
                        />
                      )}

                      {/* Catch all */}
                      <Route
                        path="*"
                        element={
                          <Navigate
                            to={
                              isSuperAdmin ? "/dashboard" : "/dashboard-alertas"
                            }
                            replace
                          />
                        }
                      />
                    </Routes>
                  </div>
                </div>
              </main>
            </div>
          </div>
        </WebSocketLogsProvider>
      </Suspense>
    </div>
  );
}

export default App;
